import CloseIcon from "@assets/svg/CloseIcon";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import CustomInput from "@components/CustomInput/CustomInput";
import FormControl from "@components/FormControl/FormControl";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
} from "@components/ui/dialog";
import FormSelect from "@components/FormSelect/FormSelect";
import { RootState } from "@store/reducers";
import { ContractType, PositionLevel } from "@utils/constants";
import { colors } from "@utils/theme";
import {
  Briefcase,
  Building2,
  FileText,
  MapPin,
  PencilLineIcon,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { updateAspiration } from "@endpoints/candidate.service";
import { setAspirationDetails } from "@store/actions/auth.action";

const aspirationDetailsDummy = {
  _id: "696a62c3159cc5b7dd4a1e06",
  candidateId: "67d5a95cc4341e7db96dd3eb",
  jobTitle: "React Developer Edit",
  industryType: "Information Technology",
  classification: {
    main: "education",
    sub: [],
  },
  positionLevel: 3,
  contractType: 1,
  skills: [
    {
      _id: "66d44707c1e15594d846eac2",
      title: "Javascript",
    },
    {
      _id: "670e6d305f9b19b2782d68db",
      title: "React native",
    },
  ],
  isStartUp: true,
  isInternship: false,
  company: "google",
  location: "United Kingdom",
  salary: {
    min: 120,
    max: 120,
  },
  createdAt: "2026-01-16T16:09:39.911Z",
  updatedAt: "2026-01-16T16:20:43.391Z",
  isActive: true,
};

const positionOptions = [
  { label: "Intern", value: PositionLevel.INTERN },
  { label: "Fresher", value: PositionLevel.FRESHER },
  { label: "Junior", value: PositionLevel.JUNIOR },
  { label: "Associate", value: PositionLevel.ASSOCIATE },
  { label: "Mid", value: PositionLevel.MID },
  { label: "Senior", value: PositionLevel.SENIOR },
  { label: "Lead", value: PositionLevel.LEAD },
  { label: "Manager", value: PositionLevel.MANAGER },
  { label: "Sr. Manager", value: PositionLevel.SR_MANAGER },
  { label: "Director", value: PositionLevel.DIRECTOR },
  { label: "Sr. Director", value: PositionLevel.SR_DIRECTOR },
  { label: "VP", value: PositionLevel.VP },
  { label: "SVP", value: PositionLevel.SVP },
  { label: "EVP", value: PositionLevel.EVP },
  { label: "Founder", value: PositionLevel.FOUNDER },
];

const contractOptions = [
  { label: "Contract", value: ContractType.CONTRACT },
  { label: "Part-Time", value: ContractType.PARTTIME },
  { label: "Permanent", value: ContractType.PERMANENT },
];

const targetFieldRows = [
  [
    { key: "industry", label: "Target Industry", icon: Briefcase },
    { key: "seniority", label: "Target Seniority", icon: TrendingUp },
    { key: "location", label: "Preferred Location", icon: MapPin },
  ],
  [
    { key: "salary", label: "Target Salary", icon: Wallet },
    { key: "company", label: "Target Company", icon: Building2 },
    { key: "contract", label: "Target Contract", icon: FileText },
  ],
];

const TargetSection = () => {
  const dispatch = useDispatch();
  const aspirationDetails = useSelector(
    (state: RootState) => state.auth.aspirationDetails,
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      jobTitle: "",
      industryType: "",
      positionLevel: "",
      location: "",

      salary: {
        min: "",
        max: "",
      },
      company: "",
      contractType: "",
    },
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSave = async (formData: any) => {
    try {
      setIsUpdating(true);
      const payload = formData;
      payload._id = aspirationDetails?._id;
      const { success, data } = await updateAspiration(payload);
      if (success) {
        dispatch(setAspirationDetails(data));
      }
    } catch (error) {
      console.error("Error updating aspiration details", error);
    } finally {
      setIsUpdating(false);
      setIsEditing(false);
      reset();
    }
  };

  // Display values derived from the fetched aspiration details
  const targetRows = useMemo(() => {
    const valueByKey: Record<string, string> = {
      industry: aspirationDetails?.industryType || "N/A",
      seniority:
        positionOptions.find(
          (position) => position.value === aspirationDetails?.positionLevel,
        )?.label || "N/A",
      location: aspirationDetails?.location || "N/A",
      salary:
        aspirationDetails?.salary?.min != null &&
        aspirationDetails?.salary?.max != null
          ? `£${Number(aspirationDetails.salary.min).toLocaleString()} - £${Number(
              aspirationDetails.salary.max,
            ).toLocaleString()}`
          : "N/A",
      company: aspirationDetails?.company || "N/A",
      contract:
        contractOptions.find(
          (contract) => contract.value === aspirationDetails?.contractType,
        )?.label || "N/A",
    };

    return targetFieldRows.map((row) =>
      row.map((field) => ({ ...field, value: valueByKey[field.key] ?? "N/A" })),
    );
  }, [aspirationDetails]);

  // Hydrate the edit form whenever the details change
  useEffect(() => {
    if (!aspirationDetails) return;

    reset({
      jobTitle: aspirationDetails.jobTitle ?? "",
      industryType: aspirationDetails.industryType ?? "",
      positionLevel: aspirationDetails.positionLevel ?? "",
      location: aspirationDetails.location ?? "",
      salary: {
        min: aspirationDetails.salary?.min?.toString() ?? "",
        max: aspirationDetails.salary?.max?.toString() ?? "",
      },
      company: aspirationDetails.company ?? "",
      contractType: aspirationDetails.contractType ?? "",
    });
  }, [aspirationDetails, reset]);

  return (
    <div className="flex flex-col border rounded-xl bg-white p-6 gap-3 relative">
      <div className="flex items-center justify-between">
        <span className="uppercase text-xs font-bold text-slate-600">
          My target role
        </span>
        <button
          onClick={() => setIsEditing(true)}
          className="hover:bg-gray-100 h-8 w-8 rounded-md flex items-center justify-center absolute right-3 top-3"
        >
          <PencilLineIcon className="h-5 w-5" />
        </button>
      </div>
      <span className="text-slate-800 text-3xl font-bold">
        {aspirationDetails?.jobTitle}
      </span>
      <div className="border-t flex flex-col gap-5 justify-between pt-5 mt-2">
        {targetRows?.map((row, rowIndex) => {
          return (
            <div key={rowIndex} className="flex justify-between">
              {row?.map((target) => {
                return (
                  <div
                    key={target?.key}
                    className="flex items-center gap-2.5 flex-1"
                  >
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border bg-gray-100">
                      <target.icon className="h-5 w-5 text-slate-600" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-bold text-gray-600">
                        {target?.label}
                      </span>
                      <span className="text-xs font-medium text-slate-800 line-clamp-2">
                        {target?.value}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent showCloseButton={false} className="bg-white !max-w-3xl">
          <div className="flex justify-between items-start mb-5">
            <div className="flex flex-col gap-0.5">
              <span className="font-semibold text-lg">Your Target Role</span>
              <span className="font-medium text-xs text-light-black">
                Update the details that define the roles you're aiming for.
              </span>
            </div>
            <DialogClose asChild>
              <button
                onClick={() => setIsEditing(false)}
                className="h-max p-2 rounded-md hover:bg-gray-50"
              >
                <CloseIcon className={"w-5 h-5"} />
              </button>
            </DialogClose>
          </div>
          <div className="bg-[#F9FAFB] p-5 rounded-md flex flex-col gap-6">
            <div className="flex items-start gap-5">
              <FormControl
                label="Job Title"
                name="jobTitle"
                placeholder={"e.g. Cybersecurity Engineer"}
                control={control}
                errors={errors}
                rules={{
                  required: "This field is required.",
                }}
              />
              <FormControl
                label="Target Industry"
                name="industryType"
                placeholder={"e.g. Information Technology"}
                control={control}
                errors={errors}
                rules={{
                  required: "This field is required.",
                }}
              />
            </div>
            <div className="flex items-start gap-5">
              <FormSelect
                label="Target Seniority"
                name="positionLevel"
                placeholder={"Select seniority level"}
                options={positionOptions}
                control={control}
                errors={errors}
                rules={{
                  required: "This field is required.",
                }}
              />
              <FormControl
                label="Preferred Location"
                name="location"
                placeholder={"e.g. London, United Kingdom"}
                control={control}
                errors={errors}
                rules={{
                  required: "This field is required.",
                }}
              />
            </div>
            <div className="flex items-start gap-5">
              <FormControl
                label="Min Salary"
                name="salary.min"
                placeholder={"e.g. 80,000"}
                control={control}
                errors={errors}
                rules={{
                  required: "This field is required.",
                }}
              />
              <FormControl
                label="Max Salary"
                name="salary.max"
                placeholder={"e.g. 100,000"}
                control={control}
                errors={errors}
                rules={{
                  required: "This field is required.",
                }}
              />
            </div>
            <div className="flex items-start gap-5">
              <FormControl
                label="Target Company"
                name="company"
                placeholder={"e.g. Google, Microsoft"}
                control={control}
                errors={errors}
                rules={{
                  required: "This field is required.",
                }}
              />
              <FormSelect
                label="Target Contract"
                name="contractType"
                placeholder={"Select contract type"}
                options={contractOptions}
                control={control}
                errors={errors}
                rules={{
                  required: "This field is required.",
                }}
              />
            </div>
          </div>
          <DialogFooter className="mt-5 gap-3">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="font-semibold text-sm text-slate-700 border py-2 px-5 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit(handleSave)}
              disabled={isUpdating}
              className="font-semibold text-sm bg-primary text-white py-2 px-5 rounded-md flex items-center gap-2 disabled:opacity-60"
            >
              {isUpdating && <LoadingAnimation color={colors.White} />}
              Save Changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TargetSection;
